/**
 * @interface Boost
 * @property {number} id
 * @property {number} post_id
 * @property {string|null} sponsor_name
 * @property {number|null} budget
 * @property {number} budget_used
 * @property {number} range
 * @property {Date|null} expires_at
 */

(function () {
    angular.module('tradersyard').factory('AdsService', AdsServiceFactory);

    AdsServiceFactory.$inject = ['$window', '$q', '$http', '$localStorage', 'graph-post-factory'];

    /**
     * @param {angular.IWindowService} $window
     * @param {angular.IQService} $q
     * @param {angular.IHttpService} $http
     * @param {WindowLocalStorage} $localStorage
     * @param postFactory
     */
    function AdsServiceFactory($window, $q, $http, $localStorage, postFactory) {
        var ADS_API = '/api/v2';
        var HIT_URL = url('metrics/hit');

        var METRIC_ENGAGEMENT = 'engagements';
        var METRIC_IMPRESSIONS = 'impressions';
        var METRIC_VIEWS = 'views';
        var METRIC_CLICKS = 'click';

        const REMEMBER_SECONDS = 30 * 60; // 30 minutes

        return {
            getBoostedPosts,
            getBoostedPostsWithAssignedRank,
            metricsHitImpressions,
            metricsHitViews,
            metricsHitClick,
            metricsHitEngagements,
            uniqueRandomGenerator,
            combineBoostedPostsInto,
        };

        function metricsHitImpressions(postId, boostId) {
            metricsHitRemember(METRIC_IMPRESSIONS, postId, boostId, undefined, REMEMBER_SECONDS);
        }

        function metricsHitViews(postId, boostId) {
            metricsHitRemember(METRIC_VIEWS, postId, boostId);
        }

        function metricsHitClick(postId, boostId, url) {
            metricsHitRemember(
                METRIC_CLICKS,
                postId,
                boostId,
                {
                    click: { url: url },
                },
                REMEMBER_SECONDS
            );
        }

        function metricsHitEngagements(postId, boostId) {
            metricsHitRemember(METRIC_ENGAGEMENT, postId, boostId, undefined, REMEMBER_SECONDS);
        }

        /**
         * Sends metric hit. Remembered hits are omitted.
         *
         * @param {string} counterName Name of the counter. Use one of the predefined const values.
         * @param {string|number} postId
         * @param {string|number} boostId
         * @param {{string: any}} [meta] additional data that will be merged with the request payload
         * @param {boolean} [remember=false] should the hit be remembered in localStorage
         * @param {number} [seconds] how long should we remember the HIT. NULL value means "forever"
         */
        function metricsHit(counterName, postId, boostId, meta, remember, seconds) {
            remember = !!remember;

            if (remember && !shouldHitRemember(counterName, postId, boostId, seconds)) {
                console.log('Already hit', counterName, postId, boostId);
                return;
            }

            const hitData = angular.extend(
                {},
                {
                    name: counterName,
                    post_id: postId,
                    boost_id: boostId,
                },
                meta
            );

            $http.post(HIT_URL, hitData);
        }

        /**
         * Basically same as metricsHit, but sets the `remember` flag
         */
        function metricsHitRemember(counterName, postId, boostId, meta, seconds) {
            return metricsHit(counterName, postId, boostId, meta, true, seconds);
        }

        /**
         * Checks if we should call this HIT and remembers that we did if we should.
         *
         * @param counterName
         * @param postId
         * @param boostId
         * @param {number} [seconds] How long in seconds. Empty value means "forever".
         * @return {boolean}
         */
        function shouldHitRemember(counterName, postId, boostId, seconds) {
            seconds = seconds || 0;

            const hits = $localStorage.hits || {};
            const key = [counterName, postId, boostId].join('-');
            const rememberForever = !!seconds;
            const lastHit = hits[key];

            if (lastHit && rememberForever) {
                return false; // we have a last hit already and we should remember forever
            }

            /**
             * Calculate if it's time for the next hit.
             */
            const nextHit = Date.now() - 1000 * seconds;

            if (lastHit && nextHit < lastHit) {
                return false; // already made a hit
            }

            // TODO: cleanup timers? Maybe store the nextHit time instead of lastHit time,
            //  so we can then clean the list of expired timers.

            hits[key] = Date.now();
            $localStorage.hits = hits;

            return true;
        }

        // region: BOOSTED POSTS
        // TODO: probably should split this service into tracking and boosted posts

        /**
         * Looks at `*.rank` of each `boostedPosts` item and put's it in the right position
         * of `allPosts` based on the array index.
         *
         * @param boostedPosts
         * @param allPosts
         * @return {object[]} new array with boosted posts added
         */
        function combineBoostedPostsInto(boostedPosts, allPosts) {
            const copyOfAllPosts = [...allPosts];

            boostedPosts.forEach((p) => {
                // ranks are not counted from 0, but indexes are
                const postRank = p.rank - 1;
                // we get all boosts for all pages first - we don't add them until we reach the point
                const inRange = postRank < copyOfAllPosts.length;

                if (!inRange) return;

                console.log('[BOOSTED POSTS] Injecting post for rank %s', p.rank);

                // put's boosted posts in place, each iteration respects the shift
                copyOfAllPosts.splice(postRank, 0, p);
            });

            return copyOfAllPosts;
        }

        /**
         * @param {Function} rankAssigner takes range, returns rank
         */
        function getBoostedPostsWithAssignedRank(rankAssigner) {
            const assignRank = (post) => angular.extend({}, post, { rank: rankAssigner(post.boost.range) });
            return getBoostedPosts().then((boostedPosts) => boostedPosts.map(assignRank));
        }

        /**
         * Get Post enriched with Boost info.
         *
         * @returns {angular.IPromise<object[]>}
         */
        function getBoostedPosts() {
            return getBoostProposals().then(function (boosts) {
                // we receive boost info only, we need to get post data
                const postPromises = boosts.map((boost) => getPostWithBoost(boost));
                return $q.all(postPromises);
            });
        }

        /**
         * Get proposal of an Ad for the given page.
         * @param [range] Range of boosted post
         * @returns {angular.IPromise<Boost[]>}
         */
        function getBoostProposals(range) {
            return $http.post(url('placements'), { range }).then((res) => res.data.data);
        }

        /**
         * Extends a Post with Boost data. The returning object will be then used when displaying a feed of Posts.
         * @param {Boost} boost
         * @return {angular.IPromise<object>}
         */
        function getPostWithBoost(boost) {
            return postFactory.getPostById(boost.post_id).then(function (postData) {
                // Mark this post as promoted, so it's handled correctly in the UI

                // Construct sponsor name label
                // TODO: Not the best place for this. Maybe a separate component would be nice,
                //  e.g. <ty-boost-sponsor-name boost="post.boost" />
                const sponsoredBy = boost.sponsor_name ? `Sponsored by ${boost.sponsor_name}` : 'Sponsored'; // just sponsored

                return angular.extend({}, postData, {
                    isPromoted: true,
                    sponsoredBy,
                    boost: boost,
                });
            });
        }

        // endregion BOOSTED POSTS

        /**
         * Just a helper function to stitch urls
         * @param {string|string[]} path
         * @returns {string}
         */
        function url(path) {
            return angular.isArray(path) ? [ADS_API].concat(path).join('/') : [ADS_API, path].join('/');
        }

        function uniqueRandomGenerator(min, max) {
            const picked = [];
            const getRandomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

            return () => {
                while (picked.length < max) {
                    const value = getRandomInt(min, max);

                    if (picked.indexOf(value) === -1) {
                        picked.push(value);
                        return value;
                    }
                }
            };
        }
    }
})();
